/**
 * Platform L3 sales-point contacts client (plan §6.1 / S9).
 * Absolute `/api/v1/customer-account/sales-point-contacts*` — NOT tenant ApiClient.
 * V2 — credentials jar only (no Bearer).
 */

import {
  normalizeLogoChipBackgroundSettings,
  normalizeLogoChipRimSettings,
  resolveConsumerSquareLogoUrl,
} from 'pi-kiosk-shared';
import { ApiError } from '../../shared/api/ApiClient.js';
import {
  customerEnvelopeApiError,
  customerInvalidJsonApiError,
} from '../../shared/errors/customerApiParseErrors.js';
import { stormAwareFetch } from '../../shared/http/stormAwareFetch.js';
import { platformJsonHeaders } from '../../shared/http/platformEnvelopeLocale.js';
import {
  readPendingSpContacts,
  removeActivatedPendingSpContacts,
} from './pendingSpContacts.js';
import {
  readPendingFavoriteSpContactRefs,
  syncGuestFavoriteOptOutFromAccountToggle,
} from './pendingFavoriteSpContacts.js';
import {
  SP_CONTACT_ERROR_CODES,
  type ClaimSalesPointContactsBody,
  type ClaimSalesPointContactsResultDTO,
  type PendingSpContactRef,
  type RecordSalesPointContactBody,
  type SalesPointContactItemDTO,
  type SalesPointContactsListDTO,
} from './salesPointContactsTypes.js';
import { fetchPublicCustomerSalesPointListableAllowlist } from '../shops/publicSalesPointsApi.js';
import { parseSalesPointCustomerPresence } from '../shops/customerPresence.js';
import { filterPendingFavoritesByListableDirectory } from './customerRetailListable.js';

/**
 * G3 — defensive parse at L3 client boundary.
 * Drop row when `tenantCode` or `customerShopUrl` is missing/non-string;
 * coerce nullish `tenantName` to `''`. Claim/favorite do not return this DTO.
 */
export function normalizeSalesPointContactItem(
  raw: unknown,
): SalesPointContactItemDTO | null {
  if (raw === null || typeof raw !== 'object') {
    return null;
  }
  const item = raw as Record<string, unknown>;
  const tenantCode = item.tenantCode;
  const customerShopUrl = item.customerShopUrl;
  if (typeof tenantCode !== 'string' || typeof customerShopUrl !== 'string') {
    return null;
  }
  const tenantNameRaw = item.tenantName;
  let tenantName = '';
  if (typeof tenantNameRaw === 'string') {
    tenantName = tenantNameRaw;
  }
  const salesPointId = item.salesPointId;
  const code = item.code;
  const name = item.name;
  const tenantId = item.tenantId;
  const lastInteractionAt = item.lastInteractionAt;
  if (
    typeof salesPointId !== 'number' ||
    typeof code !== 'string' ||
    typeof name !== 'string' ||
    typeof tenantId !== 'number' ||
    typeof lastInteractionAt !== 'string'
  ) {
    return null;
  }
  const customerPresence = parseSalesPointCustomerPresence(item.customerPresence);
  // A13a/A13b — ONLINE_ONLY public location stays null (never rehydrate ops address).
  const location =
    customerPresence === 'ONLINE_ONLY'
      ? null
      : typeof item.location === 'string'
        ? item.location
        : null;
  const nameLocales =
    item.nameLocales !== undefined && item.nameLocales !== null
      ? (item.nameLocales as SalesPointContactItemDTO['nameLocales'])
      : undefined;
  const isFavorite = item.isFavorite === true ? true : undefined;
  const favoriteOptOut = item.favoriteOptOut === true ? true : undefined;
  const rim = normalizeLogoChipRimSettings(item);
  const background = normalizeLogoChipBackgroundSettings(item);

  return {
    salesPointId,
    code,
    name,
    ...(nameLocales !== undefined ? { nameLocales } : {}),
    location,
    customerPresence,
    tenantId,
    tenantCode,
    tenantName,
    customerShopUrl,
    lastInteractionAt,
    ...(isFavorite !== undefined ? { isFavorite } : {}),
    ...(favoriteOptOut !== undefined ? { favoriteOptOut } : {}),
    imageUrl: typeof item.imageUrl === 'string' ? item.imageUrl : null,
    imageUrlDark: resolveConsumerSquareLogoUrl(item.imageUrlDark),
    tenantLogoUrl: resolveConsumerSquareLogoUrl(item.tenantLogoUrl),
    tenantLogoUrlDark: resolveConsumerSquareLogoUrl(item.tenantLogoUrlDark),
    ...rim,
    ...background,
  };
}

/** Normalize list payload after envelope parse — filters invalid rows; keeps wire `{success,data}` untouched. */
export function normalizeSalesPointContactsList(
  data: unknown,
): SalesPointContactsListDTO {
  const raw =
    data !== null && typeof data === 'object'
      ? (data as { items?: unknown; autoSelect?: unknown })
      : null;
  const itemsRaw = Array.isArray(raw?.items) ? raw.items : [];
  const items: SalesPointContactItemDTO[] = [];
  for (const row of itemsRaw) {
    const normalized = normalizeSalesPointContactItem(row);
    if (normalized !== null) {
      items.push(normalized);
    }
  }
  return {
    items,
    autoSelect: raw?.autoSelect === true && items.length === 1,
  };
}

const CONTACTS_PATH = '/api/v1/customer-account/sales-point-contacts';

interface SuccessEnvelope<T> {
  success: true;
  data: T;
}

interface ErrorEnvelope {
  success: false;
  error: string;
  code?: string;
}

export interface SalesPointContactsClientAuth {
  apiBaseUrl: string;
  signal?: AbortSignal;
}

function platformUrl(apiBaseUrl: string, path: string): string {
  const base = apiBaseUrl.replace(/\/+$/, '');
  return `${base}${path}`;
}

function authFetchInit(
  init: {
    method: string;
    headers?: Record<string, string>;
    body?: string;
    signal?: AbortSignal;
  },
): RequestInit {
  const headers: Record<string, string> = platformJsonHeaders(init.headers ?? {});
  return {
    method: init.method,
    headers,
    credentials: 'include',
    ...(init.body !== undefined ? { body: init.body } : {}),
    ...(init.signal !== undefined ? { signal: init.signal } : {}),
  };
}

async function parseEnvelope<T>(response: Response): Promise<T> {
  let body: SuccessEnvelope<T> | ErrorEnvelope;
  try {
    body = (await response.json()) as SuccessEnvelope<T> | ErrorEnvelope;
  } catch {
    throw customerInvalidJsonApiError(response.status);
  }

  if (!response.ok || body.success !== true) {
    throw customerEnvelopeApiError(response.status, body);
  }

  return body.data;
}

/** GET `/api/v1/customer-account/sales-point-contacts` */
export async function fetchSalesPointContacts(
  input: SalesPointContactsClientAuth,
): Promise<SalesPointContactsListDTO> {
  const response = await stormAwareFetch(
    platformUrl(input.apiBaseUrl, CONTACTS_PATH),
    authFetchInit({
      method: 'GET',
      signal: input.signal,
    }),
  );
  const data = await parseEnvelope<unknown>(response);
  return normalizeSalesPointContactsList(data);
}

/** POST `/api/v1/customer-account/sales-point-contacts/record` */
export async function recordSalesPointContact(
  input: SalesPointContactsClientAuth & { body: RecordSalesPointContactBody },
): Promise<void> {
  const response = await stormAwareFetch(
    platformUrl(input.apiBaseUrl, `${CONTACTS_PATH}/record`),
    authFetchInit({
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(input.body),
      signal: input.signal,
    }),
  );
  await parseEnvelope<unknown>(response);
}

/** POST `/api/v1/customer-account/sales-point-contacts/claim` */
export async function claimSalesPointContacts(
  input: SalesPointContactsClientAuth & { body: ClaimSalesPointContactsBody },
): Promise<ClaimSalesPointContactsResultDTO> {
  const response = await stormAwareFetch(
    platformUrl(input.apiBaseUrl, `${CONTACTS_PATH}/claim`),
    authFetchInit({
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(input.body),
      signal: input.signal,
    }),
  );
  return parseEnvelope<ClaimSalesPointContactsResultDTO>(response);
}

/** POST `/api/v1/customer-account/sales-point-contacts/favorite` */
export async function setSalesPointFavorite(
  input: SalesPointContactsClientAuth & {
    body:
      | { salesPointId: number; favorite: boolean }
      | { tenantCode: string; salesPointSlug: string; favorite: boolean };
  },
): Promise<{ salesPointId: number; isFavorite: boolean; favoriteOptOut: boolean }> {
  const response = await stormAwareFetch(
    platformUrl(input.apiBaseUrl, `${CONTACTS_PATH}/favorite`),
    authFetchInit({
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(input.body),
      signal: input.signal,
    }),
  );
  const result = await parseEnvelope<{
    salesPointId: number;
    isFavorite: boolean;
    favoriteOptOut: boolean;
  }>(response);
  // G7: BE favoriteOptOut alone is not enough after logout — sync guest LS opt-out.
  const body = input.body;
  syncGuestFavoriteOptOutFromAccountToggle(
    {
      salesPointId: result.salesPointId,
      ...('tenantCode' in body
        ? { tenantCode: body.tenantCode, salesPointSlug: body.salesPointSlug }
        : {}),
    },
    result.isFavorite,
  );
  return result;
}

/** MEMBERSHIP_BLOCKED must never toast (plan §3.3). */
export function isMembershipBlockedError(error: unknown): boolean {
  return (
    error instanceof ApiError && error.code === SP_CONTACT_ERROR_CODES.MEMBERSHIP_BLOCKED
  );
}

/** Claim/record failed-item codes that must stay silent (no toast). */
export function isSilentSpContactErrorCode(code: string | undefined): boolean {
  return code === SP_CONTACT_ERROR_CODES.MEMBERSHIP_BLOCKED;
}

/**
 * Toast i18n key for record/claim transport failures.
 * Returns `null` for MEMBERSHIP_BLOCKED — callers must not toast when null.
 */
export function spContactTransportToastKey(
  error: unknown,
  kind: 'record' | 'claim',
): 'errors.spContact.recordTransport' | 'errors.spContact.claimTransport' | null {
  if (isMembershipBlockedError(error)) {
    return null;
  }
  return kind === 'record'
    ? 'errors.spContact.recordTransport'
    : 'errors.spContact.claimTransport';
}

/** Client-side N-list filter (plan §3.2 / §6.8) — platform list filtered by tenantCode. */
export function filterContactsByTenantCode(
  items: readonly SalesPointContactItemDTO[],
  tenantCode: string,
): SalesPointContactItemDTO[] {
  const normalized = tenantCode.trim().toLowerCase();
  if (normalized.length === 0) {
    return [...items];
  }
  return items.filter((item) => {
    const code = item.tenantCode;
    return typeof code === 'string' && code.trim().toLowerCase() === normalized;
  });
}

/**
 * GET contacts hook — Spec A chrome enablement lives in
 * `./useSalesPointContactsByToken` (session after `/me` only).
 * Re-export kept for call-site compat / CSP import graph.
 */
export { useSalesPointContactsByToken } from './useSalesPointContactsByToken.js';

/** POST record — swallow MEMBERSHIP_BLOCKED (never toast); rethrow others for transport toast. */
export async function recordSalesPointContactSilent(
  input: SalesPointContactsClientAuth & { body: RecordSalesPointContactBody },
): Promise<void> {
  try {
    await recordSalesPointContact(input);
  } catch (err) {
    if (isMembershipBlockedError(err)) {
      return;
    }
    throw err;
  }
}

function pendingContactDedupeKey(contact: PendingSpContactRef): string {
  if (typeof contact.salesPointId === 'number' && contact.salesPointId > 0) {
    return `id:${contact.salesPointId}`;
  }
  const slug = (contact.salesPointSlug ?? '').trim().toLowerCase();
  return `slug:${contact.tenantCode.trim().toLowerCase()}|${slug}`;
}

/**
 * Claim pending visit-staged contacts only (not guest favourites).
 * Guest favourites transfer once on successful registration — see
 * `transferGuestFavoritesOnRegistrationSilent`.
 */
export async function claimPendingSpContactsSilent(
  input: SalesPointContactsClientAuth,
): Promise<void> {
  const pending = readPendingSpContacts();
  if (pending.length === 0) {
    return;
  }
  try {
    const result = await claimSalesPointContacts({
      ...input,
      body: { contacts: pending },
    });
    removeActivatedPendingSpContacts(result.activated);
    /* failed[] MEMBERSHIP_BLOCKED rows stay silent — do not toast */
  } catch (err) {
    if (isMembershipBlockedError(err)) {
      return;
    }
    /* transport / rate-limit — leave pending; surface for toast */
    throw err;
  }
}

/** Marker: guest favourites already copied for this customer (Wave F key-set). */
export const GUEST_FAVORITES_TRANSFERRED_CUSTOMER_KEY =
  'customer:guestFavoritesTransferredCustomerId';

interface GuestFavoritesTransferMarker {
  customerId: number;
  /** Dedupe keys already transferred — new staged keys still apply. */
  keys: string[];
}

function readTransferMarker(): GuestFavoritesTransferMarker | null {
  if (typeof window === 'undefined') {
    return null;
  }
  try {
    const raw = window.localStorage.getItem(GUEST_FAVORITES_TRANSFERRED_CUSTOMER_KEY);
    if (raw === null || raw.length === 0) {
      return null;
    }
    // Legacy one-shot: bare customerId string — treat as empty key set (re-apply pending).
    const asNumber = Number(raw);
    if (Number.isFinite(asNumber) && asNumber > 0 && /^\d+$/.test(raw.trim())) {
      return { customerId: asNumber, keys: [] };
    }
    const parsed = JSON.parse(raw) as unknown;
    if (parsed === null || typeof parsed !== 'object') {
      return null;
    }
    const row = parsed as { customerId?: unknown; keys?: unknown };
    if (typeof row.customerId !== 'number' || !(row.customerId > 0)) {
      return null;
    }
    const keys = Array.isArray(row.keys)
      ? row.keys.filter((k): k is string => typeof k === 'string' && k.length > 0)
      : [];
    return { customerId: row.customerId, keys };
  } catch {
    return null;
  }
}

function writeTransferMarker(marker: GuestFavoritesTransferMarker): void {
  if (typeof window === 'undefined') {
    return;
  }
  try {
    window.localStorage.setItem(
      GUEST_FAVORITES_TRANSFERRED_CUSTOMER_KEY,
      JSON.stringify(marker),
    );
  } catch {
    /* quota / private mode */
  }
}

/**
 * Every establish (login / OTP / OIDC / registration): copy **new** browser guest
 * favourites into the account. Does **not** clear localStorage.
 * Marker tracks transferred dedupe keys per customerId so newly staged items
 * still apply on a later establish.
 *
 * G6 — prefilter against public Obchody allowlist (same SoT as `/shops`) before
 * claim/star. Non-listable LS favourites are skipped; their keys are burned so
 * we do not retry delisted shops. Allowlist fetch failure does not burn keys.
 */
export async function transferGuestFavoritesOnRegistrationSilent(
  input: SalesPointContactsClientAuth & { customerId: number },
): Promise<void> {
  const marker = readTransferMarker();
  const alreadyKeys =
    marker !== null && marker.customerId === input.customerId
      ? new Set(marker.keys)
      : new Set<string>();
  const favorites = readPendingFavoriteSpContactRefs();
  const pendingNew = favorites.filter(
    (fav) => !alreadyKeys.has(pendingContactDedupeKey(fav)),
  );
  if (pendingNew.length === 0) {
    // Keep marker in sync for this customer (empty key set OK).
    if (marker === null || marker.customerId !== input.customerId) {
      writeTransferMarker({ customerId: input.customerId, keys: [...alreadyKeys] });
    }
    return;
  }

  let allowlist: Awaited<ReturnType<typeof fetchPublicCustomerSalesPointListableAllowlist>>;
  try {
    allowlist = await fetchPublicCustomerSalesPointListableAllowlist(input.apiBaseUrl);
  } catch {
    // Fail closed: do not claim/star without a fresh Obchody allowlist; retry later.
    return;
  }
  const allowedIds = new Set(allowlist.salesPointIds);
  const allowedSlugKeys = new Set(allowlist.tenantSlugKeys);
  const listablePending = filterPendingFavoritesByListableDirectory(
    pendingNew,
    allowedIds,
    allowedSlugKeys,
  );
  const pendingNewKeys = pendingNew.map(pendingContactDedupeKey);
  if (listablePending.length === 0) {
    // Dropped/skipped non-listable — burn keys so we do not retry forever.
    writeTransferMarker({
      customerId: input.customerId,
      keys: [...new Set([...alreadyKeys, ...pendingNewKeys])].sort(),
    });
    return;
  }

  const favoriteKeys = new Set(listablePending.map(pendingContactDedupeKey));
  const contacts = listablePending.slice(0, 20);
  try {
    const result = await claimSalesPointContacts({
      ...input,
      body: { contacts },
    });
    for (const activated of result.activated) {
      const key = pendingContactDedupeKey(activated);
      if (!favoriteKeys.has(key)) {
        continue;
      }
      try {
        if (typeof activated.salesPointId === 'number' && activated.salesPointId > 0) {
          await setSalesPointFavorite({
            ...input,
            body: { salesPointId: activated.salesPointId, favorite: true },
          });
        } else if (
          typeof activated.salesPointSlug === 'string' &&
          activated.salesPointSlug.length > 0
        ) {
          await setSalesPointFavorite({
            ...input,
            body: {
              tenantCode: activated.tenantCode,
              salesPointSlug: activated.salesPointSlug,
              favorite: true,
            },
          });
        }
      } catch (favErr) {
        if (isMembershipBlockedError(favErr)) {
          continue;
        }
        throw favErr;
      }
    }
    // Browser list intentionally retained for guest browsing on this device.
    // Burn listable keys that transferred + non-listable keys that were skipped.
    writeTransferMarker({
      customerId: input.customerId,
      keys: [...new Set([...alreadyKeys, ...pendingNewKeys])].sort(),
    });
  } catch (err) {
    if (isMembershipBlockedError(err)) {
      // Do not burn transferred keys — retry on a later establish.
      return;
    }
    throw err;
  }
}
